import React from 'react';
import { Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import LegalLine from './LegalLine';

import { PAGE_SIZE } from '../constants';
import { NAV } from '../navLabels';
import RedDot from './RedDot';

/**
 * The mobile navigation sheet.
 *
 * Replaces Semantic's `<Menu vertical>`, whose defaults were the whole complaint: it
 * rendered destinations at **12px in 24px rows** — smaller than their own section headers,
 * and well under the 44px minimum touch target — in grey-on-grey that read as disabled,
 * with `.ui.vertical.menu .item > i.icon { float: right }` detaching every icon from its
 * label. Those are baked into its stylesheet, so fixing them meant fighting it with
 * overrides rather than styling a list.
 *
 * This is a plain grouped list instead: 17px labels, 50px rows, the icon beside the label
 * in a tinted square, a chevron to say the row navigates. It lives in its own file because
 * Navbar.js already carries two fully duplicated menu trees and this is the longer of them.
 *
 * An admin sees 20+ rows here — far more than a phone screen — so the body scrolls
 * independently and the page behind it is locked while it is open.
 */

const ROW_HEIGHT = 50;

const Row = ({ to, icon, label, onNavigate, danger, badge }) => {
  const body = (
    <>
      <span
        style={{
          width: '30px',
          height: '30px',
          borderRadius: '8px',
          backgroundColor: danger ? '#FFEBEA' : '#EAF3FF',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon
          name={icon}
          style={{
            margin: 0,
            fontSize: '14px',
            color: danger ? '#D70015' : '#007AFF',
            width: 'auto',
            height: 'auto',
            lineHeight: 1, // same reason as the chevron below
          }}
        />
      </span>
      {/* minWidth:0 + ellipsis: 제품 지식 vs 판매 스킬 상관관계 is 20 characters and would
          otherwise push the chevron off the row. */}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: '17px',
          color: danger ? '#D70015' : '#1d1d1f',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      {badge && <RedDot />}
      {/* lineHeight:1 is what actually centres it. Semantic gives `i.icon` height:1em but
          inherits the row's 20px line-height, so inside a 12px-tall box the glyph paints
          on a 20px line and sits ~4px low. Flex centres the element, not the character —
          the box measured dead centre while the chevron visibly did not. */}
      {to && (
        <Icon
          name="chevron right"
          style={{
            margin: 0,
            fontSize: '12px',
            color: '#c7c7cc',
            lineHeight: 1,
            height: '12px',
            width: '12px',
          }}
        />
      )}
    </>
  );

  const style = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: `${ROW_HEIGHT}px`,
    padding: '0 16px',
    backgroundColor: '#ffffff',
    color: 'inherit',
    textDecoration: 'none',
    cursor: 'pointer',
    width: '100%',
    border: 'none',
    textAlign: 'left',
    fontFamily: 'inherit',
  };

  return to ? (
    <Link to={to} onClick={onNavigate} style={style}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onNavigate} style={style}>
      {body}
    </button>
  );
};

const Section = ({ title, items, onNavigate }) => (
  <div style={{ marginBottom: '22px' }}>
    {title && (
      <div
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#86868b',
          padding: '0 16px 8px',
        }}
      >
        {title}
      </div>
    )}
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        overflow: 'hidden',
        margin: '0 12px',
      }}
    >
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {/* Hairline inset past the icon, the way a grouped list separates rows without
              cutting the whole card in half. */}
          {i > 0 && (
            <div style={{ height: '1px', backgroundColor: '#f0f0f2', marginLeft: '58px' }} />
          )}
          <Row {...item} onNavigate={item.onClick || onNavigate} />
        </React.Fragment>
      ))}
    </div>
  </div>
);

const MobileNavMenu = ({
  user,
  partner,
  showKnowledgeTraining,
  contractorLabel,
  onNavigate,
  onLogout,
  hasUnreadSupport,
  topOffset = 49,
}) => {
  // The page behind a full-height sheet must not scroll with it — otherwise a flick
  // inside the menu moves the dashboard underneath and the menu appears to stick.
  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const sections = [];

  const learning = [
    { to: '/simulation', icon: 'comment', label: NAV.ROLEPLAY },
    ...(showKnowledgeTraining
      ? [{ to: '/quiz/dashboard', icon: 'student', label: NAV.QUIZ }]
      : []),
    {
      to: `/chatsessionslist?page=1&page_size=${PAGE_SIZE}`,
      icon: 'comments outline',
      label: NAV.SESSIONS,
    },
  ];
  sections.push({ title: '교육', items: learning });

  if (user.is_staff || user.is_contractor_admin) {
    const operations = [
      ...(user.is_superuser || user.is_contractor_admin
        ? [{ to: '/admin/contractors', icon: 'building', label: contractorLabel }]
        : []),
      ...(user.is_superuser
        ? [{ to: '/admin/support-tickets', icon: 'life ring', label: '지원 요청' }]
        : []),
      {
        to: `/userslist?page=1&page_size=${PAGE_SIZE}`,
        icon: 'users',
        label: NAV.MY_STAFF,
      },
    ];
    sections.push({ title: '관리자 · 운영', items: operations });

    if (user.is_staff) {
      sections.push({
        title: '관리자 · 분석',
        items: [
          {
            to: `/partnerevaluatedsessionlist?page=1&page_size=${PAGE_SIZE}`,
            icon: 'file alternate outline',
            label: 'AI vs 수기 평가 상세 비교',
          },
          { to: '/alignment-study', icon: 'chart line', label: 'AI vs 수기 평가 일치도 분석' },
          { to: '/pk-alignment-study', icon: 'chart bar', label: '제품 지식 vs 판매 스킬 상관관계' },
          {
            to: `/customerpersonaslist?page=1&page_size=${PAGE_SIZE}`,
            icon: 'id card outline',
            label: '페르소나',
          },
          {
            to: `/evaluationcriteriagrouplist?page=1&page_size=${PAGE_SIZE}`,
            icon: 'tasks',
            label: '평가 기준',
          },
        ],
      });
      sections.push({
        title: '관리자 · 제품 지식',
        items: [
          { to: '/admin/products', icon: 'box', label: '제품 정보 관리' },
          { to: '/admin/quiz-questions', icon: 'question circle', label: '퀴즈 질문' },
          { to: '/admin/quiz-attempts', icon: 'history', label: '퀴즈 내역' },
        ],
      });
    }
  }

  sections.push({
    title: '내 학습',
    items: [
      { to: '/my-feedback', icon: 'comment alternate', label: '내 피드백', badge: hasUnreadSupport },
      { to: '/userdashboard', icon: 'dashboard', label: NAV.DASHBOARD },
    ],
  });

  sections.push({
    items: [{ icon: 'log out', label: '로그아웃', danger: true, onClick: onLogout }],
  });

  return (
    <div
      style={{
        position: 'fixed',
        top: `${topOffset}px`,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f2f2f7',
        // Above SupportFeedbackButton, which is fixed at z-index 1000 and comes later in
        // the DOM — at equal z-index it won, and floated over the menu rows.
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: '18px 16px 20px' }}>
        <div style={{ fontSize: '20px', fontWeight: 600, color: '#1d1d1f' }}>
          {user.full_name || user.username}
        </div>
        {partner?.name && (
          <div style={{ fontSize: '14px', color: '#86868b', marginTop: '2px' }}>
            {partner.name}
          </div>
        )}
      </div>

      {/* The scrolling half. An admin's list is ~20 rows at 50px — twice a phone screen —
          so this pane scrolls rather than the sheet growing past the viewport. The bottom
          padding clears the iPhone home indicator, which would otherwise sit on top of
          로그아웃. */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
        }}
      >
        {sections.map((section, i) => (
          <Section
            key={section.title || `section-${i}`}
            title={section.title}
            items={section.items}
            onNavigate={onNavigate}
          />
        ))}
        {/* Was the site-wide footer. onClick closes the sheet when a legal link is tapped. */}
        <div onClick={onNavigate}>
          <LegalLine style={{ marginTop: '8px' }} />
        </div>
      </div>
    </div>
  );
};

export default MobileNavMenu;
